"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const easeOutBezier: [number, number, number, number] = [0.16, 1, 0.3, 1];
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: (customIndex: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.5,
        delay: shouldReduceMotion ? 0 : customIndex * 0.08,
        ease: easeOutBezier
      }
    })
  };
  const floatingAnimation = shouldReduceMotion ? {} : {
    y: [0, -6, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut" as const
    }
  };
  return <section id="herointro" data-nav="light" className="relative overflow-hidden bg-background pt-32 pb-20 md:pt-36 md:pb-28 lg:pt-40 lg:pb-32 isolate">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-10 xl:gap-14">
          {/* Left Column: 5 Cols on Desktop */}
          <div className="flex flex-col items-start lg:col-span-5">
            {/* Category / Location Badge */}
            <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUpVariant} className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5">
              <Sparkles className="size-3.5 text-primary" />
              <span className="text-xs font-semibold tracking-wider text-primary uppercase">
                Brookhaven, GA
              </span>
            </motion.div>

            {/* Main Editorial Headline */}
            <motion.h1 custom={1} initial="hidden" animate="visible" variants={fadeUpVariant} className="font-serif text-4xl leading-[1.12] font-semibold tracking-tight text-foreground sm:text-5xl lg:text-[3.25rem]">
              Bathroom remodeling in Brookhaven, GA
            </motion.h1>

            {/* Architectural Gold Hairline */}
            <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUpVariant} className="mt-6 h-[1px] w-24 bg-primary/40" />

            {/* Supporting Copy */}
            <motion.p custom={3} initial="hidden" animate="visible" variants={fadeUpVariant} className="mt-6 text-base leading-relaxed text-foreground/80 sm:text-lg">
              Turn your master suite into a calm, private retreat with custom
              double vanities, architectural lighting, and tailored stone tilework.
            </motion.p>

            {/* CTAs */}
            <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUpVariant} className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-lg border border-primary/80 bg-primary px-7 py-3.5 font-sans font-semibold text-primary-foreground shadow-sm transition-[color,background-color,border-color,box-shadow] hover:bg-primary/90 hover:shadow-md">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an Estimate
                </Link>
              </Button>

              <Button asChild variant="ghost" size="lg" className="group rounded-lg border border-border px-5 py-3.5 font-sans font-medium text-foreground transition-[color,background-color,border-color] hover:border-primary/50 hover:bg-secondary/5">
                <Link to="/OurProjectsPage" className="inline-flex items-center gap-2">
                  <span>Explore transformations</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Right Column: 7 Cols on Desktop */}
          <div className="relative lg:col-span-7">
            <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUpVariant} className="relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-xl">
              {/* Luxury Master Bathroom Architectural Photograph */}
              <div className="aspect-[4/3] w-full overflow-hidden bg-muted sm:aspect-[16/11]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_master_bath_retreat.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_master_bath_retreat.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_master_bath_retreat.png-wvc-sizes" alt="Luxury master bathroom in Brookhaven with freestanding tub, walk-in curbless shower, and brass fixtures" loading="eager" className="h-full w-full object-cover object-center" />
              </div>

              {/* Inner ring overlay */}
              <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-black/5 ring-inset" />

              {/* Floating Atelier Glass Card Badge */}
              <motion.div animate={floatingAnimation} className="absolute right-4 bottom-4 left-4 max-w-sm rounded-2xl border border-white/80 bg-white/75 p-5 shadow-lg backdrop-blur-md sm:right-6 sm:bottom-6 sm:left-auto">
                <div className="flex items-start gap-3.5">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <Sparkles className="size-5" />
                  </div>
                  <div>
                    <h2 className="font-serif text-base font-semibold text-foreground">
                      Curated bespoke details
                    </h2>
                    <p className="mt-1 text-xs leading-relaxed text-foreground/75">
                      Freestanding sculptural tub, curbless frameless glass
                      shower, unlacquered brass, and custom rift-sawn oak vanities.
                    </p>
                    <div className="mt-3 flex items-center gap-3 border-t border-border/50 pt-2 text-[11px] font-medium text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="size-3 text-primary" />
                        Zero-threshold entry
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="size-3 text-primary" />
                        Honed stone
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Subtle Architectural Accent Backdrop */}
            <div aria-hidden="true" className="pointer-events-none absolute -top-6 -right-6 -z-10 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
          </div>
        </div>
      </div>
    </section>;
}